import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { AdminShell } from "@/components/admin/AdminShell";
import { Seo } from "@/components/Seo";

export default function Settings() {
  const [categories, setCategories] = useState([]);
  const [threshold, setThreshold] = useState(70);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/scoring-config").then((r) => { setCategories(r.data.categories); setThreshold(r.data.pass_threshold); });
  }, []);

  const setWeight = (key, w) => setCategories((cs) => cs.map((c) => c.key === key ? { ...c, weight: Number(w) } : c));
  const setLabel = (key, l) => setCategories((cs) => cs.map((c) => c.key === key ? { ...c, label: l } : c));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/scoring-config", { categories, pass_threshold: Number(threshold) });
      toast.success("Scoring settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const totalWeight = categories.reduce((s, c) => s + Number(c.weight || 0), 0);

  return (
    <AdminShell title="Scoring Settings" actions={
      <button onClick={save} disabled={saving} data-testid="save-settings" className="inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-2.5 font-mono-pm text-xs tracking-widest uppercase hover:bg-[#991B1B] disabled:opacity-50"><Save size={14} /> {saving ? "Saving…" : "Save"}</button>}>
      <Seo title="Scoring Settings" noindex />
      <div className="max-w-2xl">
        <p className="font-mono-pm text-xs text-stone-500 mb-6 leading-relaxed">
          The PM Test Score is a weighted average across these categories, normalized to a 0–100 scale.
          Adjust labels, weights, and the pass threshold here without touching code. Changes apply to how every review's overall score is calculated.
        </p>

        <div className="border border-stone-900 bg-white p-5 mb-6">
          <label className="font-mono-pm text-[11px] uppercase tracking-widest text-stone-600 block mb-2">Pass Threshold (out of 100)</label>
          <input type="number" min="0" max="100" value={threshold} onChange={(e) => setThreshold(e.target.value)} data-testid="threshold-input"
            className="w-28 border border-stone-900 px-3 py-2 font-mono-pm text-center" />
          <p className="font-mono-pm text-[10px] text-stone-400 mt-2">A product scoring at or above this earns "Passes The PM Test".</p>
        </div>

        <div className="border border-stone-900 bg-white">
          <div className="bg-stone-900 text-white grid grid-cols-[1fr_100px] font-mono-pm text-[10px] tracking-widest uppercase">
            <div className="p-3">Category</div><div className="p-3 text-center">Weight</div>
          </div>
          {categories.map((c) => (
            <div key={c.key} className="grid grid-cols-[1fr_100px] border-t border-stone-200 items-center">
              <input value={c.label} onChange={(e) => setLabel(c.key, e.target.value)} className="p-3 font-sans-pm text-sm focus:outline-none focus:bg-stone-50" />
              <input type="number" min="0" step="0.5" value={c.weight} onChange={(e) => setWeight(c.key, e.target.value)} className="p-3 text-center font-mono-pm border-l border-stone-200 focus:outline-none focus:bg-stone-50" />
            </div>
          ))}
          <div className="grid grid-cols-[1fr_100px] border-t border-stone-900 bg-stone-50 font-mono-pm text-xs">
            <div className="p-3 uppercase tracking-widest text-stone-500">Total weight</div>
            <div className="p-3 text-center font-bold">{totalWeight}</div>
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
